import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import SearchInput from "@/components/SearchInput";
import { useSearch } from "@/lib/search";

/**
 * The one multi-select, for every list of ids a screen has to pick several of.
 *
 * Five places needed one at once — a training's units and its departments, and the
 * register's employee, unit and department filters. `lib/search.js` exists because four
 * hand-rolled searches had already drifted apart; five hand-rolled multi-selects would
 * have gone the same way, and the one that ends up subtly different is the one nobody
 * notices until it returns the wrong list to an auditor.
 *
 * Ids in, ids out. `options` is `[{ id, name, hint? }]`. A Radix Select cannot do this:
 * it closes on choose and holds a single value, which is the whole reason this exists
 * rather than a second `<Select multiple>`.
 */

const OPTION_FIELDS = ["name", "hint"];

export default function MultiSelect({
  value = [],
  onChange,
  options = [],
  placeholder = "Select…",
  /** Shown in place of the list when there is nothing to choose from. */
  empty = "Nothing to choose from yet.",
  /** Named in the summary: "3 units". Singular; the count decides the plural. */
  noun = "selected",
  searchPlaceholder = "Search…",
  disabled = false,
  className,
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const chosen = useMemo(() => new Set(value), [value]);
  const shown = useSearch(options, q, OPTION_FIELDS);

  const toggle = (id) =>
    onChange(chosen.has(id) ? value.filter((v) => v !== id) : [...value, id]);

  // The names, not the count, whenever they fit — "Unit 1, Unit 3" tells you what you
  // filtered by; "2 units" makes you open the menu again to find out.
  const names = value
    .map((id) => options.find((o) => o.id === id)?.name)
    .filter(Boolean);
  const summary =
    names.length === 0 ? placeholder
      : names.length <= 2 ? names.join(", ")
      : `${names.length} ${noun}s`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={placeholder}
          data-testid={testid}
          className={cn(
            "w-full justify-between font-normal",
            names.length === 0 && "text-muted-foreground",
            className,
          )}
        >
          <span className="truncate">{summary}</span>
          <span className="flex items-center gap-1 shrink-0">
            {names.length > 0 && (
              // A span, not a button: a <button> inside a <button> is invalid markup and
              // the browser hoists it out, which drops the trigger's own click handler.
              <span
                role="button"
                tabIndex={0}
                aria-label={`Clear ${placeholder}`}
                data-testid={testid ? `${testid}-clear` : undefined}
                onClick={(e) => { e.stopPropagation(); onChange([]); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onChange([]); }
                }}
                className="p-0.5 rounded text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="w-3.5 h-3.5" />
              </span>
            )}
            <ChevronDown className="w-4 h-4 opacity-50" aria-hidden="true" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[240px] p-0" align="start">
        {options.length === 0 ? (
          <p className="px-3 py-4 text-sm text-muted-foreground" data-testid={testid ? `${testid}-empty` : undefined}>
            {empty}
          </p>
        ) : (
          <>
            <div className="p-2 border-b border-border">
              <SearchInput value={q} onChange={setQ} placeholder={searchPlaceholder}
                           testid={testid ? `${testid}-search` : undefined} />
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {shown.map((o) => (
                <label
                  key={o.id}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-muted cursor-pointer text-sm"
                  data-testid={testid ? `${testid}-option-${o.id}` : undefined}
                >
                  <Checkbox checked={chosen.has(o.id)} onCheckedChange={() => toggle(o.id)} />
                  <span className="min-w-0">
                    <span className="block truncate">{o.name}</span>
                    {o.hint && <span className="block text-xs text-muted-foreground truncate">{o.hint}</span>}
                  </span>
                </label>
              ))}
              {shown.length === 0 && (
                <p className="px-3 py-4 text-sm text-muted-foreground">Nothing matches "{q}".</p>
              )}
            </div>
            <div className="flex items-center justify-between border-t border-border px-2 py-1.5 text-xs">
              <span className="text-muted-foreground">
                {value.length} {noun}{value.length === 1 ? "" : "s"} selected
              </span>
              <Button type="button" variant="ghost" size="sm" className="h-7"
                      onClick={() => setOpen(false)}>
                <Check className="w-3.5 h-3.5 mr-1" /> Done
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
